import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle, FileText, ShieldCheck, XCircle } from 'lucide-react';
import { Logo } from '../../auth/components/Logo';
import { publicSigningApi, SignaturePreview } from '../api/public-signing';

interface AssinaturaPublicaPageProps {
  /** Token do link, extraído de `/assinatura/:token` antes do AppRouter montar. */
  token: string;
}

type Step = 'loading' | 'error' | 'preview' | 'code' | 'signed' | 'rejected';

const appDescription = import.meta.env.VITE_APP_DESCRIPTION || 'Sistema de Gestão';

export const AssinaturaPublicaPage: React.FC<AssinaturaPublicaPageProps> = ({ token }) => {
  const [step, setStep] = useState<Step>('loading');
  const [preview, setPreview] = useState<SignaturePreview | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    publicSigningApi
      .getPreview(token)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);
        if (data.status === 'signed') setStep('signed');
        else if (data.status === 'rejected') setStep('rejected');
        else setStep('preview');
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setErrorMessage(err.message || 'Link inválido ou expirado.');
        setStep('error');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleRequestCode = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await publicSigningApi.requestCode(token);
      setCodeSent(true);
      setStep('code');
    } catch (err) {
      setErrorMessage((err as Error).message || 'Não foi possível enviar o código.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await publicSigningApi.confirm(token, code);
      setStep('signed');
    } catch (err) {
      setErrorMessage((err as Error).message || 'Código inválido.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      await publicSigningApi.reject(token, rejectReason);
      setStep('rejected');
    } catch (err) {
      setErrorMessage((err as Error).message || 'Não foi possível registrar a recusa.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md md:max-w-lg">
        <div className="bg-white py-8 px-6 shadow-lg rounded-lg">
          <div className="flex justify-center mb-6">
            <Logo className="h-16" />
          </div>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Assinatura de contrato</h2>
            <p className="mt-2 text-sm text-gray-600">{appDescription}</p>
          </div>

          {step === 'loading' && (
            <div className="flex justify-center py-6">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 border-t-transparent" />
            </div>
          )}

          {step === 'error' && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <p className="text-sm text-red-800 flex items-start">
                <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                {errorMessage}
              </p>
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-6">
              <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
                <p className="text-sm text-blue-900">
                  Olá, <strong>{preview.signatoryName}</strong>. Você foi indicado(a) como
                  signatário do contrato <strong>{preview.contractCode}</strong>.
                </p>
              </div>
              {preview.documentUrl && (
                <a
                  href={preview.documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 px-4 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Revisar documento
                </a>
              )}

              {errorMessage && (
                <p className="text-sm text-red-700">{errorMessage}</p>
              )}

              {!showRejectForm ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={handleRequestCode}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-60"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    {isSubmitting ? 'Enviando código…' : 'Confirmar assinatura'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRejectForm(true)}
                    className="w-full py-2 text-sm text-gray-500 hover:text-red-600 transition-colors"
                  >
                    Não concordo com este contrato
                  </button>
                </div>
              ) : (
                <form onSubmit={handleReject} className="space-y-3">
                  <textarea
                    required
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Descreva o motivo da recusa"
                    className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-3 px-4 border border-transparent rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-60"
                  >
                    Confirmar recusa
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRejectForm(false)}
                    className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Voltar
                  </button>
                </form>
              )}
            </div>
          )}

          {step === 'code' && (
            <form onSubmit={handleConfirm} className="space-y-6">
              <div className="rounded-md bg-blue-50 p-4 border border-blue-200">
                <p className="text-sm text-blue-900">
                  {codeSent && 'Enviamos um código de 6 dígitos para o seu e-mail. '}
                  Digite o código para confirmar sua assinatura.
                </p>
              </div>
              <input
                required
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000"
                className="w-full text-center text-2xl tracking-[0.5em] rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {errorMessage && <p className="text-sm text-red-700">{errorMessage}</p>}
              <button
                type="submit"
                disabled={isSubmitting || code.length !== 6}
                className="w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors disabled:opacity-60"
              >
                {isSubmitting ? 'Confirmando…' : 'Confirmar código'}
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleRequestCode}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Reenviar código
              </button>
            </form>
          )}

          {step === 'signed' && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <p className="text-sm text-green-800 flex items-start">
                <CheckCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                Assinatura confirmada com sucesso. Você já pode fechar esta página.
              </p>
            </div>
          )}

          {step === 'rejected' && (
            <div className="rounded-md bg-gray-100 p-4 border border-gray-200">
              <p className="text-sm text-gray-700 flex items-start">
                <XCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                Assinatura recusada. O responsável pelo contrato foi notificado.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssinaturaPublicaPage;
